/**
 * ArtRequirements.jsx — Phase 9 Special Requirements tab (2026-02).
 *
 * PRD v2 §5.4. List (default) + Grid views. Group-by
 * Category/Priority/Status ("Shoot day" is disabled — scenes.shoot_day
 * doesn't exist on the schema). Filter chip bar + search.
 *
 * Uses RequirementActionsContext to open the shared modal for create
 * and edit; new requirements append to the local list via onCreated.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Search, Grid2X2, List as ListIcon, Pencil, MoreHorizontal } from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import { useProjectRole } from '../../hooks/useProjectRole';
import { useAuth } from '../../context/AuthContext';
import {
  REQ_CATEGORIES, REQ_CATEGORY_TOKENS,
  REQ_PRIORITY_ORDER, REQ_PRIORITY_TOKENS,
  REQ_STATUS_ORDER, REQ_STATUS_TOKENS,
  ReqPriorityPill, ReqCategoryPill, ReqStatusPill,
} from './artShared';
import { useRequirementActions } from './RequirementActionsContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RequirementRow({ req, sceneMap, locationMap, onEdit, onDelete, canWrite }) {
  const pri = REQ_PRIORITY_TOKENS[req.priority] || REQ_PRIORITY_TOKENS.standard;
  const hero = req.photos?.[0]?.storage_path;
  const cat = REQ_CATEGORY_TOKENS[req.category] || REQ_CATEGORY_TOKENS.other;
  const [menu, setMenu] = useState(false);

  return (
    <div className="flex items-stretch border border-[var(--color-border)] rounded overflow-hidden bg-[var(--color-panel)] hover:border-[var(--color-accent)] transition mb-2"
      data-testid={`requirement-row-${req.id}`}>
      {/* Priority color bar */}
      <div style={{ width: 4, background: pri.bar, flexShrink: 0 }} />

      {/* Thumbnail */}
      <div style={{ width: 120, height: 100, flexShrink: 0, background: 'var(--color-bg)' }}
        className="flex items-center justify-center">
        {hero ? (
          <AuthImage path={hero} className="w-full h-full object-cover" alt="" />
        ) : (
          <span className="text-[10px] font-bold" style={{ color: cat.fg }}>{cat.icon}</span>
        )}
      </div>

      {/* Middle — clickable body */}
      <button type="button" onClick={() => onEdit(req)}
        className="flex-1 min-w-0 text-left px-3 py-2"
        data-testid={`requirement-row-body-${req.id}`}>
        <div className="flex items-center gap-1 flex-wrap mb-1">
          <ReqPriorityPill priority={req.priority} />
          <ReqCategoryPill category={req.category} />
        </div>
        <div className="text-sm font-bold text-[var(--color-text)] truncate">{req.title}</div>
        {req.description && (
          <div className="text-[11px] text-[var(--color-text-sec)] line-clamp-2 mt-0.5">
            {req.description}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap mt-1">
          {(req.linked_scene_ids || []).slice(0, 6).map(sid => (
            <span key={sid} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-text-sec)]">
              #{sceneMap.get(sid) ?? sid.slice(0, 4)}
            </span>
          ))}
          {(req.linked_location_ids || []).slice(0, 3).map(lid => (
            <span key={lid} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-text-sec)]">
              📍 {locationMap.get(lid) ?? lid.slice(0, 6)}
            </span>
          ))}
        </div>
      </button>

      {/* Right */}
      <div className="flex flex-col items-end justify-between p-2" style={{ width: 200, flexShrink: 0 }}>
        <ReqStatusPill status={req.status} />
        {req.assigned_to && (
          <div className="text-[10px] text-[var(--color-text-dim)] truncate w-full text-right">
            {req.assigned_to}
          </div>
        )}
        <div className="flex gap-1 mt-auto">
          {canWrite && (
            <button type="button" onClick={() => onEdit(req)}
              className="p-1 text-[var(--color-text-sec)] hover:text-[var(--color-accent)]"
              title="Edit"
              data-testid={`requirement-row-edit-${req.id}`}>
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {canWrite && (
            <div className="relative">
              <button type="button" onClick={() => setMenu(m => !m)}
                className="p-1 text-[var(--color-text-sec)] hover:text-[var(--color-text)]"
                title="More"
                data-testid={`requirement-row-menu-${req.id}`}>
                <MoreHorizontal className="w-3 h-3" />
              </button>
              {menu && (
                <div className="absolute top-full right-0 mt-1 z-20 bg-[var(--color-panel)] border border-[var(--color-border)] rounded shadow-lg min-w-[120px]"
                  onClick={(e) => e.stopPropagation()}>
                  <button type="button"
                    onClick={() => { setMenu(false); onDelete(req); }}
                    className="w-full text-left text-[11px] px-2 py-1.5 text-[var(--color-priority-critical)] hover:bg-[var(--color-panel)]"
                    data-testid={`requirement-row-delete-${req.id}`}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RequirementCard({ req, onEdit }) {
  const pri = REQ_PRIORITY_TOKENS[req.priority] || REQ_PRIORITY_TOKENS.standard;
  const hero = req.photos?.[0]?.storage_path;
  const cat = REQ_CATEGORY_TOKENS[req.category] || REQ_CATEGORY_TOKENS.other;
  return (
    <button type="button" onClick={() => onEdit(req)}
      className="text-left rounded-lg overflow-hidden border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-accent)] transition flex"
      data-testid={`requirement-card-${req.id}`}>
      <div style={{ width: 4, background: pri.bar, flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <div className="relative aspect-[4/3] bg-[var(--color-bg)]">
          {hero ? (
            <AuthImage path={hero} className="w-full h-full object-cover" alt="" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl" style={{ color: cat.fg }}>
              {cat.icon}
            </div>
          )}
          <div className="absolute top-2 right-2">
            <ReqStatusPill status={req.status} />
          </div>
        </div>
        <div className="p-2">
          <div className="flex items-center gap-1 flex-wrap mb-1">
            <ReqPriorityPill priority={req.priority} />
            <ReqCategoryPill category={req.category} />
          </div>
          <div className="text-sm font-bold text-[var(--color-text)] truncate">{req.title}</div>
          {req.description && (
            <div className="text-[11px] text-[var(--color-text-sec)] line-clamp-2 mt-0.5">
              {req.description}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

export default function ArtRequirements({ projectId, token }) {
  const [view, setView] = useState(() => localStorage.getItem('art.requirements.view') || 'list');
  useEffect(() => { localStorage.setItem('art.requirements.view', view); }, [view]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('');
  const [linkedSceneId, setLinkedSceneId] = useState('');
  const [groupBy, setGroupBy] = useState('category');

  const { permissions } = useProjectRole(projectId);
  const { user } = useAuth();
  // Art module (2026-07) — write affordances open to any authenticated user
  // with project access. Backend still enforces auth.
  const canWrite = !!user || !!permissions?.canWrite;

  const { openRequirementModal, scenes, locations } = useRequirementActions();

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const sceneMap = useMemo(() => {
    const m = new Map();
    for (const s of scenes) m.set(s.id, s.scene_number);
    return m;
  }, [scenes]);
  const locationMap = useMemo(() => {
    const m = new Map();
    for (const l of locations) if (l.id) m.set(l.id, l.name);
    return m;
  }, [locations]);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (category) params.category = category;
      if (priority) params.priority = priority;
      if (status) params.status = status;
      if (linkedSceneId) params.linked_scene_id = linkedSceneId;
      const r = await authAxios.get(`/projects/${projectId}/art/requirements`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authAxios, projectId, search, category, priority, status, linkedSceneId]);
  useEffect(() => { reload(); }, [reload]);

  const openCreate = useCallback((prefill = null) => {
    openRequirementModal({
      mode: 'create', prefill,
      onCreated: (saved) => onSaved(saved),
    });
  }, [openRequirementModal]);
  const openEdit = useCallback((r) => {
    openRequirementModal({
      mode: 'edit', req: r,
      onCreated: (saved) => onSaved(saved),
    });
  }, [openRequirementModal]);

  const onSaved = (saved) => {
    setItems(prev => {
      const i = prev.findIndex(a => a.id === saved.id);
      if (i < 0) { setTotal(t => t + 1); return [saved, ...prev]; }
      const nx = prev.slice(); nx[i] = saved; return nx;
    });
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await authAxios.delete(`/projects/${projectId}/art/requirements/${r.id}`);
      setItems(prev => prev.filter(x => x.id !== r.id));
      setTotal(t => Math.max(0, t - 1));
      toast.success('Deleted');
    } catch (e) { console.error(e); toast.error('Delete failed'); }
  };

  // Header count summary
  const summary = useMemo(() => {
    const crit = items.filter(r => r.priority === 'critical').length;
    const needing = items.filter(r => r.status === 'needed' || r.status === 'prepped').length;
    return { crit, needing };
  }, [items]);

  // Grouping
  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: null, items }];
    if (groupBy === 'priority') {
      const buckets = REQ_PRIORITY_ORDER.map(p => ({
        key: p,
        label: REQ_PRIORITY_TOKENS[p].label + ` (${items.filter(r => r.priority === p).length})`,
        color: REQ_PRIORITY_TOKENS[p].fg,
        items: items.filter(r => r.priority === p),
      }));
      return buckets;
    }
    if (groupBy === 'status') {
      return REQ_STATUS_ORDER.map(s => ({
        key: s,
        label: REQ_STATUS_TOKENS[s].label + ` (${items.filter(r => r.status === s).length})`,
        color: REQ_STATUS_TOKENS[s].fg,
        items: items.filter(r => r.status === s),
      }));
    }
    // Default: category
    return REQ_CATEGORIES.map(c => ({
      key: c,
      label: REQ_CATEGORY_TOKENS[c].label + ` (${items.filter(r => r.category === c).length})`,
      color: REQ_CATEGORY_TOKENS[c].fg,
      items: items.filter(r => r.category === c),
    }));
  }, [items, groupBy]);

  return (
    <div className="p-4" data-testid="art-requirements-view">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 border border-[var(--color-border)] rounded px-2 py-1 bg-[var(--color-bg)]">
          <Search className="w-3 h-3 text-[var(--color-text-dim)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search requirements…"
            className="bg-transparent border-0 h-6 text-xs w-56 outline-none text-[var(--color-text)]"
            data-testid="art-requirements-search" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
          data-testid="art-requirements-cat-filter">
          <option value="">All categories</option>
          {REQ_CATEGORIES.map(k => (
            <option key={k} value={k}>{REQ_CATEGORY_TOKENS[k].label}</option>
          ))}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)}
          className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
          data-testid="art-requirements-pri-filter">
          <option value="">All priorities</option>
          {REQ_PRIORITY_ORDER.map(p => (
            <option key={p} value={p}>{REQ_PRIORITY_TOKENS[p].label}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
          data-testid="art-requirements-status-filter">
          <option value="">All statuses</option>
          {REQ_STATUS_ORDER.map(s => (
            <option key={s} value={s}>{REQ_STATUS_TOKENS[s].label}</option>
          ))}
        </select>
        <select value={linkedSceneId} onChange={(e) => setLinkedSceneId(e.target.value)}
          className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
          data-testid="art-requirements-scene-filter">
          <option value="">All linked scenes</option>
          {scenes.map(s => (
            <option key={s.id} value={s.id}>#{s.scene_number}</option>
          ))}
        </select>

        <div className="text-[10px] text-[var(--color-text-dim)] ml-2">
          {total} requirement{total === 1 ? '' : 's'}
          {summary.crit > 0 && <span className="ml-2 text-[var(--color-priority-critical)]">· {summary.crit} critical</span>}
          {summary.needing > 0 && <span className="ml-2 text-[var(--color-accent)]">· {summary.needing} needing prep</span>}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <label className="text-[10px] text-[var(--color-text-dim)]">Group by</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
            className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
            data-testid="art-requirements-groupby">
            <option value="category">Category</option>
            <option value="priority">Priority</option>
            <option value="status">Status</option>
            <option value="shoot_day" disabled title="scenes.shoot_day not available on schema">Shoot day (n/a)</option>
            <option value="none">— none —</option>
          </select>
          <div className="flex gap-1 border border-[var(--color-border)] rounded overflow-hidden">
            <button onClick={() => setView('list')}
              className={`px-2 py-1 text-xs ${view === 'list' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
              data-testid="art-requirements-view-list">
              <ListIcon className="w-3 h-3" />
            </button>
            <button onClick={() => setView('grid')}
              className={`px-2 py-1 text-xs ${view === 'grid' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
              data-testid="art-requirements-view-grid">
              <Grid2X2 className="w-3 h-3" />
            </button>
          </div>
          {canWrite && (
            <button onClick={() => openCreate(null)}
              className="text-xs px-3 py-1.5 rounded bg-[var(--color-accent)] text-black font-bold flex items-center gap-1"
              data-testid="art-requirements-add-btn">
              <Plus className="w-3 h-3" /> Add Requirement
            </button>
          )}
        </div>
      </div>

      {loading && <div className="text-center text-[var(--color-text-dim)] text-sm py-8">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center py-16" data-testid="art-requirements-empty">
          <div className="text-sm text-[var(--color-text-sec)] mb-3">
            No special requirements yet. Add one for any scene needing FX, rigging, or coordination.
          </div>
          {canWrite && (
            <button onClick={() => openCreate(null)}
              className="text-xs px-3 py-1.5 rounded bg-[var(--color-accent)] text-black font-bold inline-flex items-center gap-1"
              data-testid="art-requirements-add-btn-empty">
              <Plus className="w-3 h-3" /> Add Requirement
            </button>
          )}
        </div>
      )}

      {!loading && items.length > 0 && groups.map(g => {
        const hasItems = g.items.length > 0;
        // Suppress groups with 0 items when grouping is active — but always show if groupBy=none
        if (!hasItems && groupBy !== 'none') return null;
        return (
          <div key={g.key} className="mb-4" data-testid={`art-requirements-group-${g.key}`}>
            {g.label && (
              <div
                className="text-[10px] font-bold uppercase tracking-wider py-1 px-2 mb-2 rounded"
                style={{ color: g.color || 'var(--color-text-sec)', background: 'rgba(255,255,255,.03)' }}
              >
                {g.label}
              </div>
            )}
            {view === 'list' && (
              <div>
                {g.items.map(r => (
                  <RequirementRow key={r.id} req={r}
                    sceneMap={sceneMap} locationMap={locationMap}
                    onEdit={openEdit} onDelete={onDelete} canWrite={canWrite} />
                ))}
              </div>
            )}
            {view === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {g.items.map(r => (
                  <RequirementCard key={r.id} req={r} onEdit={openEdit} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
